import CircularLoader from "@/components/loading/LoadingComponent";
import { fetchWithCSRF } from "@/helpers/common/csrf";
import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { FiCopy, FiTrash2 } from "react-icons/fi";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "tablewind";

interface WebhookLog {
    id: string;
    method: string;
    path: string;
    content_type: string;
    source_ip: string | null;
    received_at: string;
    body_preview: string;
}

interface ColumnConfig<T> {
    label: string;
    accessor: keyof T | string;
    sortable?: boolean;
    render?: (row: T) => React.ReactNode;
}

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;
const fetcher = (url: string) => fetchWithCSRF(url).then((res) => res.json());

const methodColors: Record<string, string> = {
    GET: "bg-green-500",
    POST: "bg-blue-500",
    PUT: "bg-amber-500",
    PATCH: "bg-orange-500",
    DELETE: "bg-red-500",
    HEAD: "bg-purple-500",
    OPTIONS: "bg-slate-500",
};

export default function WebhooksPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    const triggerRefresh = useCallback(() => {
        setRefreshKey((prev) => prev + 1);
    }, []);

    const defaultParams: Record<string, string> = {
        page: "1",
        page_size: "25",
    };

    const urlParams = Object.fromEntries(searchParams.entries());

    const initialParams: Record<string, string> = {
        ...defaultParams,
        ...urlParams,
    };

    const handleDeleteAll = async () => {
        if (
            window.confirm(
                "Are you sure you want to delete ALL webhook logs? This cannot be undone.",
            )
        ) {
            try {
                await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/webhooks/`,
                    {
                        method: "DELETE",
                    },
                );
                triggerRefresh();
            } catch (error) {
                console.error("Error deleting webhooks:", error);
            }
        }
    };

    const columns = useMemo(() => {
        const baseColumns: ColumnConfig<WebhookLog>[] = [
            {
                accessor: "method",
                label: "Method",
                sortable: false,
                render: (row: WebhookLog) => (
                    <span
                        className={clsx(
                            "rounded-md px-2.5 py-1 text-xs font-semibold text-white",
                            methodColors[row.method] || "bg-slate-500",
                        )}
                    >
                        {row.method}
                    </span>
                ),
            },
            {
                accessor: "path",
                label: "Path",
                sortable: false,
                render: (row: WebhookLog) => (
                    <Link
                        to={`/longhouse/webhooks/${row.id}`}
                        className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                    >
                        {row.path}
                    </Link>
                ),
            },
            {
                accessor: "content_type",
                label: "Content Type",
                sortable: false,
                render: (row: WebhookLog) => (
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                        {row.content_type || "-"}
                    </span>
                ),
            },
            {
                accessor: "source_ip",
                label: "Source IP",
                sortable: false,
                render: (row: WebhookLog) => (
                    <span className="font-mono text-sm text-slate-500 dark:text-slate-400">
                        {row.source_ip || "-"}
                    </span>
                ),
            },
            {
                accessor: "received_at",
                label: "Received",
                sortable: false,
                render: (row: WebhookLog) => {
                    const date = new Date(row.received_at);
                    return (
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                            {date.toLocaleString()}
                        </span>
                    );
                },
            },
            {
                accessor: "body_preview",
                label: "Body Preview",
                sortable: false,
                render: (row: WebhookLog) => (
                    <span className="max-w-xs truncate text-sm text-slate-500 dark:text-slate-400">
                        {row.body_preview || "(empty)"}
                    </span>
                ),
            },
            {
                accessor: "actions",
                label: "Actions",
                sortable: false,
                render: (row: WebhookLog) => (
                    <Link
                        to={`/longhouse/webhooks/${row.id}`}
                        className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                    >
                        View
                    </Link>
                ),
            },
        ];

        return baseColumns;
    }, []);

    const filterFields = useMemo(
        () => [
            {
                name: "method",
                apiKey: "method",
                allOption: "All Methods",
                type: "select" as const,
                options: [
                    { id: "GET", name: "GET" },
                    { id: "POST", name: "POST" },
                    { id: "PUT", name: "PUT" },
                    { id: "PATCH", name: "PATCH" },
                    { id: "DELETE", name: "DELETE" },
                ],
            },
            {
                name: "path_contains",
                apiKey: "path_contains",
                type: "text" as const,
            },
        ],
        [],
    );

    const webhookUrl = `${API_URL}/longhouse-api/village/webhooks/catch`;
    const [urlCopied, setUrlCopied] = useState(false);

    const handleCopyUrl = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(webhookUrl);
            setUrlCopied(true);
            setTimeout(() => setUrlCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    }, [webhookUrl]);

    return (
        <div className="mx-auto mt-4 w-full px-4">
            {/* Endpoint Info Card */}
            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-start justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                            Webhook Capture Endpoint
                        </h2>
                        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                            Point your webhooks to this endpoint. All incoming
                            requests will be captured and logged here for
                            debugging.
                        </p>
                        <div className="mt-4 flex items-center gap-3">
                            <code className="rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                                {webhookUrl}
                            </code>
                            <button
                                onClick={handleCopyUrl}
                                className={clsx(
                                    "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                                    urlCopied
                                        ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                        : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600",
                                )}
                            >
                                <FiCopy className="h-4 w-4" />
                                {urlCopied ? "Copied!" : "Copy"}
                            </button>
                        </div>
                        <p className="mt-3 text-xs text-slate-500 dark:text-slate-500">
                            Supports any HTTP method: GET, POST, PUT, PATCH,
                            DELETE, HEAD, OPTIONS
                        </p>
                    </div>
                    <button
                        onClick={handleDeleteAll}
                        className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                    >
                        <FiTrash2 className="h-4 w-4" />
                        Delete All
                    </button>
                </div>
            </div>

            {/* Data Table */}
            <div className="rounded-2xl border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <DataTable<WebhookLog>
                    navigate={navigate}
                    pageTitle="Webhook Logs"
                    pageSubtitle="View incoming webhook requests for debugging."
                    key={refreshKey}
                    endpoint={`${API_URL}/longhouse-api/village/webhooks/`}
                    columns={columns}
                    initialQuery={initialParams}
                    fetcher={fetcher}
                    filterFields={filterFields}
                    loadingComponent={<CircularLoader />}
                    showMobileFilters={showMobileFilters}
                    setShowMobileFilters={setShowMobileFilters}
                />
            </div>
        </div>
    );
}
